import React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import {
  BulkImportSummary,
  BulkRowResult,
  PartnerResource,
} from "../../api/partners";
import { useIsMobile } from "../../hooks/useIsMobile";

interface BulkImportDialogProps {
  open: boolean;
  partnerId: string;
  partnerName: string;
  onClose: () => void;
  onImported: () => void;
}

const PLACEHOLDER = `email,displayName
ada@example.com,Ada Lovelace
grace@example.com,"Hopper, Grace"`;

// The server provisions synchronously behind API Gateway's 29s timeout, so each
// request is capped at 15 rows. We chunk the roster client-side and call the
// endpoint once per chunk (sequentially), so the dialog handles rosters of any
// size. Re-running is idempotent, so a failed chunk can be retried safely.
const CHUNK_SIZE = 15;

const statusColor = (
  status: BulkRowResult["status"]
): "success" | "info" | "default" | "error" => {
  switch (status) {
    case "created":
      return "success";
    case "attached":
      return "info";
    case "already_member":
      return "default";
    default:
      return "error";
  }
};

/** Split pasted CSV into a header line + non-empty data lines. */
const splitCsv = (csv: string): { header: string; dataLines: string[] } => {
  const lines = csv
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const [header = "", ...dataLines] = lines;
  return { header, dataLines };
};

/** Best-effort email from a CSV data line (first column), for error rows. */
const emailFromLine = (line: string): string =>
  line.split(",")[0]?.replace(/^"|"$/g, "").trim() || "(unknown)";

const summarize = (rows: BulkRowResult[]): BulkImportSummary => ({
  total: rows.length,
  created: rows.filter((r) => r.status === "created").length,
  attached: rows.filter((r) => r.status === "attached").length,
  alreadyMember: rows.filter((r) => r.status === "already_member").length,
  error: rows.filter((r) => r.status === "error").length,
  alreadyHadActiveSubscription: rows.filter(
    (r) => r.alreadyHadActiveSubscription
  ).length,
});

export const BulkImportDialog: React.FC<BulkImportDialogProps> = ({
  open,
  partnerId,
  partnerName,
  onClose,
  onImported,
}) => {
  const [csv, setCsv] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [results, setResults] = React.useState<BulkRowResult[]>();
  const [progress, setProgress] = React.useState({ processed: 0, total: 0 });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const isMobile = useIsMobile();

  const handleClose = () => {
    if (loading) return;
    setCsv("");
    setError(undefined);
    setResults(undefined);
    setProgress({ processed: 0, total: 0 });
    setPage(0);
    onClose();
  };

  const handleImport = async () => {
    const { header, dataLines } = splitCsv(csv);
    if (!header || dataLines.length === 0) {
      setError("Paste a header row plus at least one member row.");
      return;
    }
    setLoading(true);
    setError(undefined);
    setResults([]);
    setPage(0);
    setProgress({ processed: 0, total: dataLines.length });

    const accumulated: BulkRowResult[] = [];
    try {
      for (let i = 0; i < dataLines.length; i += CHUNK_SIZE) {
        const chunk = dataLines.slice(i, i + CHUNK_SIZE);
        const chunkCsv = [header, ...chunk].join("\n");
        try {
          const res = await PartnerResource.bulkImport(partnerId, chunkCsv);
          accumulated.push(...res.results);
        } catch (err) {
          // A failed chunk doesn't abort the run — record its rows as errors so
          // the admin can retry just those (re-running is idempotent).
          const message =
            err instanceof Error ? err.message : "Request failed.";
          for (const line of chunk) {
            accumulated.push({
              email: emailFromLine(line),
              displayName: "",
              status: "error",
              error: message,
            });
          }
        }
        setProgress({
          processed: Math.min(i + chunk.length, dataLines.length),
          total: dataLines.length,
        });
        setResults([...accumulated]);
      }
      onImported();
    } finally {
      setLoading(false);
    }
  };

  const summary = results ? summarize(results) : undefined;
  const pageRows = results
    ? results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Bulk Import Members — {partnerName}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Paste a roster CSV with an <code>email</code> column and a{" "}
          <code>displayName</code> (or <code>name</code>) column. Large rosters
          are imported in batches of {CHUNK_SIZE}; keep this dialog open until it
          finishes. Re-running is safe — already-provisioned members are skipped.
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={6}
          placeholder={PLACEHOLDER}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          slotProps={{
            input: { sx: { fontFamily: "monospace", fontSize: "0.85rem" } },
          }}
          disabled={loading}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Provisioning {progress.processed} / {progress.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                progress.total > 0
                  ? (progress.processed / progress.total) * 100
                  : 0
              }
            />
          </Box>
        )}

        {summary && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Chip label={`Total: ${summary.total}`} size="small" />
              <Chip
                label={`Created: ${summary.created}`}
                size="small"
                color="success"
              />
              <Chip
                label={`Attached: ${summary.attached}`}
                size="small"
                color="info"
              />
              <Chip label={`Already member: ${summary.alreadyMember}`} size="small" />
              <Chip
                label={`Errors: ${summary.error}`}
                size="small"
                color={summary.error > 0 ? "error" : "default"}
              />
              {summary.alreadyHadActiveSubscription > 0 && (
                <Chip
                  label={`Had active sub: ${summary.alreadyHadActiveSubscription}`}
                  size="small"
                  color="warning"
                />
              )}
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row, i) => (
                    <TableRow key={`${row.email}-${page * rowsPerPage + i}`}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={statusColor(row.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {row.status === "error"
                          ? row.error
                          : row.alreadyHadActiveSubscription
                            ? "Already had active subscription — promo skipped"
                            : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={results?.length ?? 0}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {results && !loading ? "Done" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !csv.trim()}
        >
          {loading
            ? `Importing ${progress.processed}/${progress.total}…`
            : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
